"use client";
import React, { useState, useEffect, useCallback } from 'react';
import { 
  ChevronDown, 
  ChevronRight, 
  Edit3, 
  ArrowLeft, 
  Save, 
  RotateCcw,
  Folder,
  FolderOpen,
  Shield,
  Users,
  Eye,
  Edit,
  Trash2
} from 'lucide-react';
import { 
  getModuleListNested, 
  useUpdateModule, 
  useSavePermission,
  useSaveBulkPermissions,
  useGetPermissionsByUsertype,
  useGetRole 
} from '@/hooks/useCommon';

// Types based on your API response
interface ApiModule {
  module_id: number;
  module_name: string;
  parent_id: number;
  controller: string | null;
  action: string | null;
  namespace: string | null;
  url: string | null;
  icon_class: string;
  menu_order: number | null;
  status: number;
  rule_id: number | null;
  order_menu: number;
  permission_status: number;
  permissions: {
    read: number;
    write: number;
    delete: number;
  };
  children: ApiModule[];
}

interface Module {
  id: string;
  name: string;
  namespace: string;
  order: number;
  url: string;
  status: 'Active' | 'De-Active';
  controller: string;
  action: string;
  iconClass: string;
  slug?: string;
  children?: Module[];
  selected?: boolean;
  existingPermissions?: {
    read: boolean;
    write: boolean;
    delete: boolean;
    order_menu?: number;
  };
}

interface PermissionData {
  module_id: number;
  module_name: string;
  parent_id: number;
  menu_order: number | null;
  rule_id: number | null;
  is_read: number | null;
  is_write: number | null;
  is_delete: number | null;
  order_menu: number | null;
  permission_status: number | null;
}

interface Role {
  role_id: number;
  role_type: number;
  RoleName: string;
  isDeleted: boolean;
  isApproved: number;
  isActive: number;
  ApprovedBy: number;
  RoleDescription: string;
  user_grade: string;
  created_date: string;
  ModifiedDate: string;
}

// Payload type for update
interface UpdateModulePayload {
  module_name: string;
  controller: string;
  namespace: string;
  action: string;
  parent_id: number;
  menu_order: number;
  icon_class: string;
  url: string;
  status: number;
  modified_by: number;
}

// Permission payload types
interface SavePermissionPayload {
  usertype_id: number;
  module_id: number;
  is_read: number;
  is_write: number;
  is_delete: number;
  order_menu: number;
  status: number;
  created_by: number;
  modified_by: number;
}

interface BulkPermissionItem {
  module_id: number;
  read: number;
  write: number;
  delete: number;
  order_menu: number;
  created_by: number;
  modified_by: number;
  status: number;
}

interface BulkPermissionsPayload {
  usertype_id: number;
  permissions: BulkPermissionItem[];
}

// Transform API module to component module
const transformApiModule = (apiModule: ApiModule, existingPermissions?: PermissionData[]): Module => {
  const modulePermission = existingPermissions?.find(p => p.module_id === apiModule.module_id);
  
  return {
    id: apiModule.module_id.toString(),
    name: apiModule.module_name,
    namespace: apiModule.namespace || '',
    order: apiModule.order_menu || apiModule.menu_order || 0,
    url: apiModule.url || '',
    status: apiModule.status === 1 ? 'Active' : 'De-Active',
    controller: apiModule.controller || '',
    action: apiModule.action || '',
    iconClass: apiModule.icon_class,
    selected: false,
    existingPermissions: modulePermission ? {
      read: modulePermission.is_read === 1,
      write: modulePermission.is_write === 1,
      delete: modulePermission.is_delete === 1,
      order_menu: modulePermission.order_menu || 0
    } : undefined,
    children: apiModule.children && apiModule.children.length > 0 
      ? apiModule.children.map(child => transformApiModule(child, existingPermissions))
      : undefined
  };
};

// Transform component module to API payload
const transformToApiPayload = (module: Module, modifiedBy: number = 1): UpdateModulePayload => {
  return {
    module_name: module.name,
    controller: module.controller,
    namespace: module.namespace,
    action: module.action,
    parent_id: 0,
    menu_order: module.order,
    icon_class: module.iconClass,
    url: module.url,
    status: module.status === 'Active' ? 1 : 0,
    modified_by: modifiedBy
  };
};

const ManageModules: React.FC = () => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [selectedModules, setSelectedModules] = useState<Set<string>>(new Set());
  const [selectedUserType, setSelectedUserType] = useState<number>(1);
  const [showPermissionsModal, setShowPermissionsModal] = useState<boolean>(false);
  const [permissionSettings, setPermissionSettings] = useState({
    read: true,
    write: false,
    delete: false,
    order_menu: 1
  });
  
  // Use the React Query hooks
  const { data: apiModules, isLoading, error, refetch } = getModuleListNested(1);
  const { data: rolesData, isLoading: rolesLoading } = useGetRole();
  const updateModuleMutation = useUpdateModule();
  const savePermissionMutation = useSavePermission();
  const saveBulkPermissionsMutation = useSaveBulkPermissions();
  const { data: existingPermissions, refetch: refetchPermissions } = useGetPermissionsByUsertype(selectedUserType);

  // Get roles from the API response - FIXED: Use useMemo to prevent unnecessary re-renders
  const roles = React.useMemo(() => {
    return rolesData?.results || [];
  }, [rolesData?.results]);

  // Transform the API data when it's available, including existing permissions
  const modules = React.useMemo(() => {
    return apiModules ? apiModules.map(module => transformApiModule(module, existingPermissions)) : [];
  }, [apiModules, existingPermissions]);

  // Update permission settings when user type changes or when a single module is selected
  useEffect(() => {
    if (selectedModules.size === 1) {
      const moduleId = Array.from(selectedModules)[0];
      const module = findModuleById(modules, moduleId);
      if (module?.existingPermissions) {
        setPermissionSettings({
          read: module.existingPermissions.read,
          write: module.existingPermissions.write,
          delete: module.existingPermissions.delete,
          order_menu: module.existingPermissions.order_menu || 1
        });
      } else {
        // Reset to default if no existing permissions
        setPermissionSettings({
          read: true,
          write: false,
          delete: false,
          order_menu: 1
        });
      }
    } else if (selectedModules.size > 1) {
      // For multiple selection, don't pre-fill but keep current settings
      // or set to sensible defaults
      setPermissionSettings(prev => ({
        ...prev,
        order_menu: 1
      }));
    }
  }, [selectedUserType, selectedModules, modules]);

  const toggleExpand = useCallback((id: string) => {
    setExpandedItems(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(id)) {
        newExpanded.delete(id);
      } else {
        newExpanded.add(id);
      }
      return newExpanded;
    });
  }, []);

  const handleEdit = useCallback((module: Module) => {
    setEditingModule({ ...module });
    setIsEditMode(true);
  }, []);

  const handleSave = () => {
    if (!editingModule) return;
    
    const payload = transformToApiPayload(editingModule, 10);
    
    updateModuleMutation.mutate(
      { 
        id: editingModule.id, 
        payload 
      },
      {
        onSuccess: (data) => {
          console.log('Module updated successfully:', data);
          setIsEditMode(false);
          setEditingModule(null);
          refetch();
        },
        onError: (error) => {
          console.error('Error updating module:', error);
        }
      }
    );
  };

  const handleReset = useCallback(() => {
    if (editingModule) {
      const original = findModuleById(modules, editingModule.id);
      if (original) {
        setEditingModule({ ...original });
      }
    }
  }, [editingModule, modules]);

  const handleBack = useCallback(() => {
    setIsEditMode(false);
    setEditingModule(null);
  }, []);

  // Helper function to find module by ID
  const findModuleById = useCallback((modules: Module[], id: string): Module | null => {
    for (const module of modules) {
      if (module.id === id) return module;
      if (module.children) {
        const found = findModuleById(module.children, id);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Handle checkbox selection - FIXED VERSION
  const handleModuleSelect = useCallback((moduleId: string, checked: boolean) => {
    setSelectedModules(prev => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(moduleId);
      } else {
        newSelected.delete(moduleId);
      }
      return newSelected;
    });
  }, []);

  // Handle select all - FIXED VERSION
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      const allIds = new Set(getAllModuleIds(modules));
      setSelectedModules(allIds);
    } else {
      setSelectedModules(new Set());
    }
  }, [modules]);

  // Handle row click - prevent checkbox toggle when clicking the row
  const handleRowClick = useCallback((moduleId: string, event: React.MouseEvent) => {
    // Only process if the click wasn't on a checkbox or button
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'BUTTON' || target.closest('button') || target.closest('input')) {
      return;
    }
    
    // Toggle expand/collapse on row click
    toggleExpand(moduleId);
  }, [toggleExpand]);

  // Handle permission settings change - FIXED: Proper event handling
  const handlePermissionChange = useCallback((permission: keyof typeof permissionSettings, value: boolean | number) => {
    setPermissionSettings(prev => ({
      ...prev,
      [permission]: value
    }));
  }, []);

  // Handle individual permission checkbox change
  const handlePermissionCheckboxChange = useCallback((permission: 'read' | 'write' | 'delete', checked: boolean) => {
    setPermissionSettings(prev => ({
      ...prev,
      [permission]: checked
    }));
  }, []);

  // Handle order menu change
  const handleOrderMenuChange = useCallback((value: number) => {
    setPermissionSettings(prev => ({
      ...prev,
      order_menu: value
    }));
  }, []);

  // Open permissions modal
  const handleOpenPermissionsModal = useCallback(() => {
    if (selectedModules.size === 1) {
      const moduleId = Array.from(selectedModules)[0];
      const module = findModuleById(modules, moduleId);
      if (module?.existingPermissions) {
        setPermissionSettings({
          read: module.existingPermissions.read,
          write: module.existingPermissions.write,
          delete: module.existingPermissions.delete,
          order_menu: module.existingPermissions.order_menu || 1
        });
      } else {
        setPermissionSettings({
          read: true,
          write: false,
          delete: false,
          order_menu: 1
        });
      }
    } else {
      // For multiple selection, set sensible defaults
      setPermissionSettings({
        read: true,
        write: false,
        delete: false,
        order_menu: 1
      });
    }
    setShowPermissionsModal(true);
  }, [selectedModules, modules, findModuleById]);

  // Save permissions for selected modules
  const handleSavePermissions = () => {
    if (selectedModules.size === 0) return;

    if (selectedModules.size === 1) {
      // Single permission save
      const moduleId = Array.from(selectedModules)[0];
      const payload: SavePermissionPayload = {
        usertype_id: selectedUserType,
        module_id: parseInt(moduleId),
        is_read: permissionSettings.read ? 1 : 0,
        is_write: permissionSettings.write ? 1 : 0,
        is_delete: permissionSettings.delete ? 1 : 0,
        order_menu: permissionSettings.order_menu,
        status: 1,
        created_by: 1,
        modified_by: 1
      };

      savePermissionMutation.mutate(payload, {
        onSuccess: (data) => {
          console.log('Permission saved successfully:', data);
          setShowPermissionsModal(false);
          setSelectedModules(new Set());
          refetchPermissions();
        },
        onError: (error) => {
          console.error('Error saving permission:', error);
        }
      });
    } else {
      // Bulk permissions save
      const permissions: BulkPermissionItem[] = Array.from(selectedModules).map(moduleId => ({
        module_id: parseInt(moduleId),
        read: permissionSettings.read ? 1 : 0,
        write: permissionSettings.write ? 1 : 0,
        delete: permissionSettings.delete ? 1 : 0,
        order_menu: permissionSettings.order_menu,
        created_by: 1,
        modified_by: 1,
        status: 1
      }));

      const payload: BulkPermissionsPayload = {
        usertype_id: selectedUserType,
        permissions
      };

      saveBulkPermissionsMutation.mutate(payload, {
        onSuccess: (data) => {
          console.log('Bulk permissions saved successfully:', data);
          setShowPermissionsModal(false);
          setSelectedModules(new Set());
          refetchPermissions();
        },
        onError: (error) => {
          console.error('Error saving bulk permissions:', error);
        }
      });
    }
  };

  // Render permission badges
  const renderPermissionBadges = useCallback((module: Module) => {
    if (!module.existingPermissions) {
      return (
        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded border">
          No permissions
        </span>
      );
    }

    return (
      <div className="flex items-center space-x-1">
        <div className={`flex items-center space-x-1 px-2 py-1 rounded border ${
          module.existingPermissions.read ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
        }`}>
          <Eye className={`w-3 h-3 ${module.existingPermissions.read ? 'text-green-600' : 'text-gray-400'}`} />
          <span className={`text-xs ${module.existingPermissions.read ? 'text-green-700' : 'text-gray-500'}`}>
            Read
          </span>
        </div>
        <div className={`flex items-center space-x-1 px-2 py-1 rounded border ${
          module.existingPermissions.write ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
        }`}>
          <Edit className={`w-3 h-3 ${module.existingPermissions.write ? 'text-blue-600' : 'text-gray-400'}`} />
          <span className={`text-xs ${module.existingPermissions.write ? 'text-blue-700' : 'text-gray-500'}`}>
            Write
          </span>
        </div>
        <div className={`flex items-center space-x-1 px-2 py-1 rounded border ${
          module.existingPermissions.delete ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
        }`}>
          <Trash2 className={`w-3 h-3 ${module.existingPermissions.delete ? 'text-red-600' : 'text-gray-400'}`} />
          <span className={`text-xs ${module.existingPermissions.delete ? 'text-red-700' : 'text-gray-500'}`}>
            Delete
          </span>
        </div>
        {module.existingPermissions.order_menu && (
          <div className="flex items-center space-x-1 px-2 py-1 rounded border bg-purple-50 border-purple-200">
            <span className="text-xs text-purple-700">
              Order: {module.existingPermissions.order_menu}
            </span>
          </div>
        )}
      </div>
    );
  }, []);

  const renderModuleTree = useCallback((modules: Module[], depth = 0) => {
    return modules.map((module) => (
      <div key={module.id}>
        <div 
          className={`flex items-center justify-between p-3 border-b border-gray-200 hover:bg-gray-50 cursor-pointer ${
            depth > 0 ? 'ml-' + (depth * 6) : ''
          }`}
          style={{ paddingLeft: `${depth * 24 + 12}px` }}
          onClick={(e) => handleRowClick(module.id, e)}
        >
          <div className="flex items-center space-x-3 flex-1">
            {/* Checkbox - FIXED: Added stopPropagation */}
            <div onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={selectedModules.has(module.id)}
                onChange={(e) => {
                  e.stopPropagation();
                  handleModuleSelect(module.id, e.target.checked);
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
              />
            </div>
            
            {module.children && module.children.length > 0 ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(module.id);
                }}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
              >
                {expandedItems.has(module.id) ? (
                  <ChevronDown className="w-4 h-4 text-gray-600" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                )}
              </button>
            ) : (
              <div className="w-6"></div>
            )}
            
            {module.children ? (
              expandedItems.has(module.id) ? (
                <FolderOpen className="w-5 h-5 text-blue-500" />
              ) : (
                <Folder className="w-5 h-5 text-blue-500" />
              )
            ) : (
              <div className="w-5 h-5 bg-gray-300 rounded-sm"></div>
            )}
            
            <div className="flex flex-col">
              <span className="text-sm font-medium text-gray-800">{module.name}</span>
              {module.url && module.url !== '/' && (
                <span className="text-xs text-gray-500 mt-1">{module.url}</span>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Permission Badges */}
            {renderPermissionBadges(module)}
            
            <div className="flex items-center space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(module);
                }}
                className="p-2 hover:bg-blue-50 rounded text-blue-600 transition-colors"
                disabled={updateModuleMutation.isPending}
                title="Edit Module"
              >
                <Edit3 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        
        {module.children && expandedItems.has(module.id) && (
          <div>
            {renderModuleTree(module.children, depth + 1)}
          </div>
        )}
      </div>
    ));
  }, [selectedModules, expandedItems, handleRowClick, handleModuleSelect, toggleExpand, renderPermissionBadges, handleEdit, updateModuleMutation.isPending]);

  // Loading state
  if (isLoading || rolesLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6 bg-white">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-600">Loading modules and roles...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6 bg-white">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-700 font-medium">Error loading modules</div>
          <div className="text-red-600 text-sm mt-1">{error.message}</div>
          <button
            onClick={() => refetch()}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (isEditMode && editingModule) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-white">
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 mb-4"
            disabled={updateModuleMutation.isPending}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
          <h2 className="text-2xl font-bold text-gray-800">Edit Module</h2>
          {updateModuleMutation.isPending && (
            <div className="mt-2 text-sm text-blue-600">Saving changes...</div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-[10px] mb-1 block">
                Module Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editingModule.name}
                onChange={(e) => setEditingModule({...editingModule, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={updateModuleMutation.isPending}
              />
            </div>

            <div>
              <label className="text-[10px] mb-1 block">
                Module Namespace <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editingModule.namespace}
                onChange={(e) => setEditingModule({...editingModule, namespace: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={updateModuleMutation.isPending}
              />
            </div>

            <div>
              <label className="text-[10px] mb-1 block">
                Order <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={editingModule.order}
                onChange={(e) => setEditingModule({...editingModule, order: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={updateModuleMutation.isPending}
              />
            </div>

            <div>
              <label className="text-[10px] mb-1 block">
                Url <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editingModule.url}
                onChange={(e) => setEditingModule({...editingModule, url: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={updateModuleMutation.isPending}
              />
            </div>

            <div>
              <label className="text-[10px] mb-1 block">
                Status <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-6">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name={`status-${editingModule.id}`}
                    value="Active"
                    checked={editingModule.status === 'Active'}
                    onChange={(e) => setEditingModule({...editingModule, status: e.target.value as 'Active' | 'De-Active'})}
                    className="mr-2"
                    disabled={updateModuleMutation.isPending}
                  />
                  <span className="text-sm">Active</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name={`status-${editingModule.id}`}
                    value="De-Active"
                    checked={editingModule.status === 'De-Active'}
                    onChange={(e) => setEditingModule({...editingModule, status: e.target.value as 'Active' | 'De-Active'})}
                    className="mr-2"
                    disabled={updateModuleMutation.isPending}
                  />
                  <span className="text-sm">De-Active</span>
                </label>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-[10px] mb-1 block">
                Controller Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editingModule.controller}
                onChange={(e) => setEditingModule({...editingModule, controller: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="controller name"
                disabled={updateModuleMutation.isPending}
              />
            </div>

            <div>
              <label className="text-[10px] mb-1 block">
                Action <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editingModule.action}
                onChange={(e) => setEditingModule({...editingModule, action: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={updateModuleMutation.isPending}
              />
            </div>

            <div>
              <label className="text-[10px] mb-1 block">
                Icon Class <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editingModule.iconClass}
                onChange={(e) => setEditingModule({...editingModule, iconClass: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={updateModuleMutation.isPending}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-8">
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 flex items-center space-x-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
            disabled={updateModuleMutation.isPending}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center space-x-2 disabled:bg-green-300 disabled:cursor-not-allowed"
            disabled={updateModuleMutation.isPending}
          >
            <RotateCcw className="w-4 h-4" />
            <span>Reset</span>
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 flex items-center space-x-2 disabled:bg-orange-300 disabled:cursor-not-allowed"
            disabled={updateModuleMutation.isPending}
          >
            <Save className="w-4 h-4" />
            <span>
              {updateModuleMutation.isPending ? 'Saving...' : 'Save'}
            </span>
          </button>
        </div>

        {updateModuleMutation.isError && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-red-700 font-medium">Error saving module</div>
            <div className="text-red-600 text-sm mt-1">
              There was an error updating the module. Please try again.
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 bg-white">
      {/* Permissions Modal */}
      {showPermissionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <Shield className="w-5 h-5 mr-2" />
              Set Permissions
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  User Role
                </label>
                <select
                  value={selectedUserType}
                  onChange={(e) => setSelectedUserType(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {roles.map((role) => (
                    <option key={role.role_id} value={role.role_id}>
                      {role.RoleName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Permissions
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={permissionSettings.read}
                      onChange={(e) => handlePermissionCheckboxChange('read', e.target.checked)}
                      className="mr-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                    />
                    <span>Read Access</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={permissionSettings.write}
                      onChange={(e) => handlePermissionCheckboxChange('write', e.target.checked)}
                      className="mr-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                    />
                    <span>Write Access</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={permissionSettings.delete}
                      onChange={(e) => handlePermissionCheckboxChange('delete', e.target.checked)}
                      className="mr-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                    />
                    <span>Delete Access</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Menu Order
                </label>
                <input
                  type="number"
                  value={permissionSettings.order_menu}
                  onChange={(e) => handleOrderMenuChange(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowPermissionsModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePermissions}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2"
                disabled={savePermissionMutation.isPending || saveBulkPermissionsMutation.isPending}
              >
                <Shield className="w-4 h-4" />
                <span>
                  {savePermissionMutation.isPending || saveBulkPermissionsMutation.isPending 
                    ? 'Saving...' 
                    : `Save (${selectedModules.size})`}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Role Selector */}
      <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">Module Management</h2>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Users className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Viewing permissions for:</span>
              <select
                value={selectedUserType}
                onChange={(e) => setSelectedUserType(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                {roles.map((role) => (
                  <option key={role.role_id} value={role.role_id}>
                    {role.RoleName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200 p-4 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-800">Modules</h3>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="select-all"
                checked={selectedModules.size > 0 && selectedModules.size === getAllModuleIds(modules).length}
                onChange={(e) => handleSelectAll(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
              />
              <label htmlFor="select-all" className="text-sm text-gray-700 cursor-pointer">
                Select All
              </label>
              {selectedModules.size > 0 && (
                <span className="text-sm text-gray-500">
                  ({selectedModules.size} selected)
                </span>
              )}
            </div>
            
            {selectedModules.size > 0 && (
              <button
                onClick={handleOpenPermissionsModal}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2 text-sm"
              >
                <Shield className="w-4 h-4" />
                <span>Set Permissions</span>
              </button>
            )}
          </div>
        </div>
        
        <div className="divide-y divide-gray-200">
          {modules.length > 0 ? (
            renderModuleTree(modules)
          ) : (
            <div className="p-8 text-center text-gray-500">
              <FolderOpen className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-lg">No modules found</p>
              <p className="text-sm mt-2">There are no modules available to display.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function to get all module IDs recursively
const getAllModuleIds = (modules: Module[]): string[] => {
  const ids: string[] = [];
  
  const collectIds = (moduleList: Module[]) => {
    moduleList.forEach(module => {
      ids.push(module.id);
      if (module.children) {
        collectIds(module.children);
      }
    });
  };
  
  collectIds(modules);
  return ids;
};

export default ManageModules;